import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ResizableImage from '../components/ResizableImageExtension';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import api from '../utils/api';
import EditorToolbar from '../components/EditorToolbar';
import { ArrowLeft, Check, Pencil, Save, X, Trash2 } from 'lucide-react';

const PRESET_COLORS = ['#818cf8','#f87171','#4ade80','#fb923c','#22d3ee','#facc15','#e879f9','#f472b6','#a78bfa','#34d399'];

function koreaToday() {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul' }).format(new Date());
}

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await api.post('/boards/upload', fd);
  return r.data.url;
}

export default function GoodsScheduleDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const backPath = searchParams.get('from') || '/work/product/goods-calendar';
  const today = koreaToday();

  const [schedule, setSchedule] = useState(null);
  const [infoForm, setInfoForm] = useState(null);
  const [editingInfo, setEditingInfo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const saveTimer = useRef(null);
  const imgInputRef = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get(`/goods-schedule/${id}`);
    setSchedule(r.data);
    setInfoForm({
      name: r.data.name,
      startDate: r.data.startDate,
      endDate: r.data.endDate,
      color: r.data.color,
      done: r.data.done,
    });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveContent = useCallback(async (html) => {
    setSaving(true);
    try {
      await api.put(`/goods-schedule/${id}`, { description: html });
      setSavedAt(new Date());
    } finally {
      setSaving(false);
    }
  }, [id]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ history: true }),
      ResizableImage,
      Placeholder.configure({ placeholder: '내용을 입력하세요...' }),
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
    ],
    content: '',
    editorProps: {
      attributes: {
        class: 'prose prose-invert max-w-none focus:outline-none min-h-[400px] px-6 py-4',
      },
      handlePaste(view, event) {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            item.getAsFile() && uploadImage(item.getAsFile()).then(url => {
              if (url) editor.chain().focus().setImage({ src: url }).run();
            });
            return true;
          }
        }
        return false;
      },
      handleDrop(view, event) {
        const files = event.dataTransfer?.files;
        if (!files?.length) return false;
        for (const file of files) {
          if (file.type.startsWith('image/')) {
            event.preventDefault();
            uploadImage(file).then(url => {
              if (url) editor.chain().focus().setImage({ src: url }).run();
            });
            return true;
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        saveContent(editor.getHTML());
      }, 2000);
    },
  });

  useEffect(() => {
    if (editor && schedule && !editor.isDestroyed) {
      const current = editor.getHTML();
      const loaded = schedule.description || '';
      if (current !== loaded) {
        editor.commands.setContent(loaded, false);
      }
    }
  }, [editor, schedule]);

  const handleImageButton = () => imgInputRef.current?.click();

  const handleImageFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) editor.chain().focus().setImage({ src: url }).run();
    e.target.value = '';
  };

  const saveInfo = async () => {
    if (!infoForm.name?.trim()) return;
    setSaving(true);
    try {
      const r = await api.put(`/goods-schedule/${id}`, infoForm);
      setSchedule(r.data);
      setEditingInfo(false);
    } finally {
      setSaving(false);
    }
  };

  const toggleDone = async () => {
    const next = !schedule.done;
    setSchedule(s => ({ ...s, done: next }));
    setInfoForm(f => ({ ...f, done: next }));
    await api.put(`/goods-schedule/${id}`, { done: next });
  };

  const handleDelete = async () => {
    if (!confirm('일정을 삭제하시겠습니까?')) return;
    await api.delete(`/goods-schedule/${id}`);
    navigate(backPath);
  };

  const statusLabel = () => {
    if (!schedule) return null;
    if (schedule.done) return { label: '완료', cls: 'text-green-500 bg-green-500/10 border-green-500/30' };
    if (schedule.endDate < today) return { label: '기간 지남', cls: 'text-red-400 bg-red-400/10 border-red-400/30' };
    if (schedule.startDate <= today && today <= schedule.endDate)
      return { label: '진행중', cls: 'text-brand bg-brand/10 border-brand/30' };
    return { label: '예정', cls: 'text-gray-400 bg-gray-800 border-gray-700' };
  };

  if (!schedule || !infoForm) {
    return <div className="flex flex-col flex-1 min-h-0 items-center justify-center"><p className="text-gray-600 text-sm">불러오는 중...</p></div>;
  }

  const st = statusLabel();

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* 헤더 */}
      <div className="flex items-center gap-3 mb-4 flex-shrink-0">
        <button onClick={() => navigate('/work/product/goods-calendar')}
          className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-gray-800 flex-shrink-0">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-bold text-white truncate">{schedule.name}</h1>
          <div className="flex items-center gap-2 mt-0.5">
            <span className="text-gray-500 text-xs">
              {schedule.startDate === schedule.endDate ? schedule.startDate : `${schedule.startDate} ~ ${schedule.endDate}`}
            </span>
            {st && <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${st.cls}`}>{st.label}</span>}
            {saving && <span className="text-gray-600 text-[10px]">저장 중...</span>}
            {!saving && savedAt && <span className="text-gray-700 text-[10px]">저장됨</span>}
          </div>
        </div>
        <button onClick={toggleDone}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors flex-shrink-0 ${
            schedule.done
              ? 'bg-green-500/15 border-green-500/40 text-green-400 hover:bg-green-500/25'
              : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500 hover:text-white'
          }`}>
          <Check size={13} strokeWidth={schedule.done ? 3 : 2} />
          {schedule.done ? '완료됨' : '완료 처리'}
        </button>
      </div>

      {/* 스크롤 영역 */}
      <div className="flex-1 overflow-y-auto space-y-4 pb-8">

        {/* 일정 정보 */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-white text-sm font-semibold">일정 정보</h3>
            {!editingInfo ? (
              <button onClick={() => setEditingInfo(true)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-white px-2 py-1 rounded hover:bg-gray-800">
                <Pencil size={12} /> 수정
              </button>
            ) : (
              <div className="flex gap-1.5">
                <button onClick={() => { setEditingInfo(false); setInfoForm({ name: schedule.name, startDate: schedule.startDate, endDate: schedule.endDate, color: schedule.color, done: schedule.done }); }}
                  className="flex items-center gap-1 text-xs text-gray-500 hover:text-white px-2 py-1 rounded hover:bg-gray-800">
                  <X size={12} /> 취소
                </button>
                <button onClick={saveInfo} disabled={saving}
                  className="flex items-center gap-1 text-xs text-brand px-2 py-1 rounded bg-brand/10 hover:bg-brand/20 disabled:opacity-50">
                  <Save size={12} /> 저장
                </button>
              </div>
            )}
          </div>
          {editingInfo ? (
            <div className="space-y-3">
              <div>
                <label className="text-gray-500 text-xs mb-1 block">일정명</label>
                <input value={infoForm.name} onChange={e => setInfoForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-gray-500 text-xs mb-1 block">시작일</label>
                  <input type="date" value={infoForm.startDate} onChange={e => setInfoForm(f => ({ ...f, startDate: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
                </div>
                <div className="flex-1">
                  <label className="text-gray-500 text-xs mb-1 block">종료일</label>
                  <input type="date" value={infoForm.endDate} onChange={e => setInfoForm(f => ({ ...f, endDate: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-brand" />
                </div>
              </div>
              <div>
                <label className="text-gray-500 text-xs mb-2 block">색상</label>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {PRESET_COLORS.map(c => (
                    <button key={c} onClick={() => setInfoForm(f => ({ ...f, color: c }))}
                      className={`w-6 h-6 rounded-full border-2 transition-transform ${infoForm.color === c ? 'border-white scale-125' : 'border-transparent hover:scale-110'}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: schedule.color }} />
              <span className="text-white text-sm">{schedule.name}</span>
              <span className="text-gray-600 text-xs ml-auto">
                {schedule.startDate} ~ {schedule.endDate}
              </span>
            </div>
          )}
        </div>

        {/* 에디터 */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <EditorToolbar editor={editor} onImageUpload={handleImageButton} />
          <input ref={imgInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageFile} />
          <EditorContent editor={editor} />
        </div>

        {/* 삭제 */}
        <div className="flex justify-end pt-2">
          <button onClick={handleDelete}
            className="flex items-center gap-1.5 text-xs text-red-500/70 hover:text-red-400 px-3 py-1.5 border border-red-500/20 hover:border-red-500/40 rounded-lg transition-colors">
            <Trash2 size={13} /> 일정 삭제
          </button>
        </div>
      </div>
    </div>
  );
}
